// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import RedirectToSignIn from "@bg/App/Components/RedirectToSignIn/RedirectToSignIn";
import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { getSettingsCollection } from "@bg/App/Store/Selectors/SettingsSelectors";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { hasLocalStorage } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isAuth: getSession(state).isAuthenticated,
    nonStockPartNumber: getSettingsCollection(state).customSettings.nonStockPartNumber,
    enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    errorWrapper?: InjectableCss;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    offerPrice?: TypographyPresentationProps;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    nonStockText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    errorWrapper: {
        css: css`
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
        weight: BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.legend,
        css: css`
            display: none;
            @media (max-width: 900px), print {
                display: block;
                margin-bottom: 4px;
            }
        `,
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    quoteMessageWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    price: {
        priceText: { weight: fontConfig.weight.bold },
        errorText: { weight: fontConfig.weight.bold },
        signInText: {
            color: "primary",
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.semiBold,
        },
        nonStockText: {
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.semiBold,
            css: css`
                max-width: 120px;
                display: block;
                color: ${getColor("danger.main")};
            `,
            ellipsis: true,
        },
        realTimeText: {
            weight: fontConfig.weight.bold,
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            size: "18px",
            lineHeight: fontConfig.lineHeight.small,
            weight: fontConfig.weight.bold,
            transform: "uppercase",
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            size: fontConfig.fontSize.xSmall,
        },
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: fontConfig.weight.bold },
        errorText: { weight: fontConfig.weight.bold },
        realTimeText: {
            weight: fontConfig.weight.bold,
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            size: 12,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: fontConfig.weight.bold,
    },
    savingsMessageText: {
        italic: true,
        size: 12,
    },
    quoteMessage: {
        tooltip: {
            cssOverrides: {
                tooltipClickable: css`
                    height: 18px;
                `,
            },
        },
        text: {
            variant: "h6",
            forwardAs: "p",
            weight: fontConfig.weight.bold,
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    offerPrice: {
        size: 22,
        weight: fontConfig.weight.bold,
        lineHeight: "18px",
        css: css`
            letter-spacing: 0;
            margin-left: 10px;
            & sup {
                font-size: ${fontConfig.fontSize.small}px;
            }
        `,
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    label,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    showSalePriceLabel = true,
    isAuth,
    nonStockPartNumber,
    enablePostalCodeLocationFinder,
    ...otherProps
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));
    const [customShowPricing, setCustomShowPricing] = React.useState(false);

    React.useEffect(() => {
        if (
            (enablePostalCodeLocationFinder && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) && !isAuth) ||
            !showPricing
        ) {
            setCustomShowPricing(false);
        } else {
            setCustomShowPricing(true);
        }
    }, []);

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    let pricing;
    let erpNumber;
    let additionalResults;
    if ("product" in pricingData) {
        pricing = pricingData.productInfo.pricing;
        erpNumber = pricingData?.product?.productNumber;
        additionalResults = pricingData?.productInfo?.pricing?.additionalResults;
    } else {
        pricing = pricingData.pricing;
        erpNumber = "";
        additionalResults = pricingData?.properties;
    }

    const unitOfMeasure = unitOfMeasureDisplay || unitOfMeasureDescription;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    // <xng-change>
    const InfoIcon: {
        [key: string]: {
            src: string;
            color: string;
        };
    } = {
        bg: {
            src: "Info",
            color: "secondary.main",
        },
        bp: {
            src: "Info",
            color: "secondary.main",
        },
        bgs: {
            src: "Info2",
            color: "tertiary.main",
        },
    };

    if (nonStockPartNumber === erpNumber) {
        // this is non stock item / Don't show pricing nor inventory
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...priceStyles.nonStockText}>
                    {siteMessage("Custom_PriceInventory_NonStockProduct")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (
        additionalResults &&
        additionalResults.noPriceFound &&
        additionalResults.noPriceFound.toLowerCase() === "true"
    ) {
        // Real time price for product failed to load. this is just for products, cartlines are handled in next if statement as property
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...priceStyles.nonStockText} className="failed-to-load">
                    {siteMessage("Custom_Price_FailedToLoad")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (
        additionalResults &&
        additionalResults.pricingFailed &&
        additionalResults.pricingFailed.toLowerCase() === "true"
    ) {
        // Real time price for carline failed to load. this is just for cartlines
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...priceStyles.nonStockText} className="failed-to-load">
                    {siteMessage("Custom_Price_FailedToLoad")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (!canSeePrices || !customShowPricing) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <RedirectToSignIn>
                    <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                        {siteMessage("Pricing_SignInForPrice")}
                    </Typography>
                </RedirectToSignIn>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.errorWrapper}>
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        const iconProps = InfoIcon[BLUEPRINT_NAME.toLowerCase()];
        return (
            <SectionWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Tooltip
                        {...quoteStyles.tooltip}
                        iconProps={{
                            ...iconProps,
                            size: 15,
                            css: css`
                                margin-right: 5px;
                            `,
                        }}
                        text={siteMessage("Rfq_TooltipMessage").toString()}
                    />
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel || cartLineModel?.salePriceLabel || translate("Sale Price:")
        : "";

    if ((otherProps as any).monthlyDeals) {
        let realPrice = currencySymbol;
        let subPrice = "";

        if (pricing && pricing.unitNetPriceDisplay) {
            [realPrice, subPrice] = pricing!.unitNetPriceDisplay!.split(".");
        }

        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...styles.offerPrice}>
                    {realPrice}.<Typography as="sup">{subPrice}</Typography>
                </Typography>
            </SectionWrapper>
        );
    }

    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{label || translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} data-test-selector="priceIncludeVat">
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    // salePriceLabel={salePriceLabel}
                />
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
}: PriceProps) => {
    return (
        <>
            {pricing ? (
                <>
                    <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice_CurrencySymbol">
                        {enableVat && displayWithVat
                            ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                            : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                    </Typography>
                    {unitOfMeasure && showUnitOfMeasure && (
                        <Typography {...styles.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
                            &nbsp;{BLUEPRINT_NAME !== "bgs" ? `/` : ""}&nbsp;
                            {unitOfMeasure}
                        </Typography>
                    )}
                </>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                    {currencySymbol}
                </Typography>
            )}
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};
interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}
interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
